import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Easing,
} from 'remotion';

const PRIMARY = '#EF4444';
const SECONDARY = '#F59E0B';
const ACCENT = '#10B981';
const BG = '#08090c';

const prepReasons = [
  { stat: '72%', label: '受访者曾遇突发状况', sub: '家庭应急意识调查（示意）' },
  { stat: '3 天', label: '建议自备基础物资天数', sub: '水、食物、药品' },
  { stat: '<3 min', label: '黄金疏散决策窗口', sub: '熟悉预案可争取时间' },
];

const kitItems = [
  { name: '饮用水', detail: '每人每日约 3L' },
  { name: '即食食品', detail: '罐头、能量棒' },
  { name: '急救包', detail: '绷带、消毒、药品' },
  { name: '手电筒', detail: '备用电池' },
  { name: '多功能工具', detail: '哨子、绳索' },
  { name: '重要证件复印件', detail: '防水袋密封' },
  { name: '保暖衣物', detail: '毯子与雨衣' },
  { name: '充电宝', detail: '保持通讯' },
];

const pyramidLevels = [
  { label: '急救与呼救', priority: 1, widthPct: 28, color: PRIMARY },
  { label: '避险与掩蔽', priority: 2, widthPct: 42, color: SECONDARY },
  { label: '饮水', priority: 3, widthPct: 56, color: '#EAB308' },
  { label: '食物', priority: 4, widthPct: 70, color: '#84CC16' },
  { label: '信息与联络', priority: 5, widthPct: 84, color: ACCENT },
];

const flowScenarios = [
  {
    title: '地震',
    steps: ['蹲下掩护抓牢', '远离玻璃与重物', '震动停止后有序撤离', '勿乘电梯'],
    color: PRIMARY,
  },
  {
    title: '火灾',
    steps: ['低姿捂口鼻', '沿疏散指示撤离', '关门阻烟', '到集合点清点'],
    color: SECONDARY,
  },
  {
    title: '洪水',
    steps: ['向高处转移', '切断电源', '勿涉水过深流', '等待救援指引'],
    color: '#06B6D4',
  },
];

const hotlines = [
  { num: '119', name: '火警', color: PRIMARY },
  { num: '120', name: '急救', color: '#F87171' },
  { num: '110', name: '报警', color: '#60A5FA' },
];

const FADE = 12;

const sectionOpacity = (
  frame: number,
  enter: number,
  exit: number,
  fade = FADE,
): number =>
  interpolate(frame, [enter - fade, enter, exit, exit + fade], [0, 1, 1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.inOut(Easing.cubic),
  });

const sectionSlide = (
  frame: number,
  enter: number,
  exit: number,
  fade = FADE,
): number =>
  interpolate(
    frame,
    [enter - fade, enter, exit, exit + fade],
    [36, 0, 0, -24],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

const CountdownRing: React.FC<{ frame: number; baseFrame: number }> = ({
  frame,
  baseFrame,
}) => {
  const rel = Math.max(0, frame - baseFrame);
  const seconds = 10;
  const cycle = seconds * 30;
  const phase = rel % cycle;
  const progress = interpolate(phase, [0, cycle - 6], [1, 0], {
    extrapolateRight: 'clamp',
    easing: Easing.linear,
  });
  const displayNum = Math.max(1, Math.ceil((phase / cycle) * seconds));
  const r = 52;
  const c = 2 * Math.PI * r;
  const dash = c * progress;

  return (
    <div style={{ position: 'relative', width: 120, height: 120 }}>
      <svg width={120} height={120} viewBox="0 0 120 120">
        <circle
          cx={60} cy={60} r={r}
          fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={7}
        />
        <circle
          cx={60} cy={60} r={r}
          fill="none" stroke={PRIMARY} strokeWidth={7}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          transform="rotate(-90 60 60)"
          style={{ filter: `drop-shadow(0 0 8px ${PRIMARY})` }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 36,
          fontWeight: 900,
          color: '#fff',
        }}
      >
        {displayNum}
      </div>
    </div>
  );
};

const PriorityPyramid: React.FC<{
  frame: number;
  baseFrame: number;
  fps: number;
}> = ({ frame, baseFrame, fps }) => {
  const w = 760;
  const h = 400;
  const cx = w / 2;
  const baseY = 370;
  const tipY = 50;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      {pyramidLevels.map((lvl, i) => {
        const s = spring({
          frame: frame - baseFrame - i * 12,
          fps,
          config: { damping: 12, stiffness: 150 },
        });
        const t = i / pyramidLevels.length;
        const nextT = (i + 1) / pyramidLevels.length;
        const y1 = baseY - t * (baseY - tipY);
        const y2 = baseY - nextT * (baseY - tipY);
        const halfW1 = (lvl.widthPct / 100) * 300;
        const nextLvl = pyramidLevels[i + 1];
        const halfW2 = nextLvl ? (nextLvl.widthPct / 100) * 300 : 0;
        const points = nextLvl
          ? `${cx - halfW1},${y1} ${cx + halfW1},${y1} ${cx + halfW2},${y2} ${cx - halfW2},${y2}`
          : `${cx - halfW1},${y1} ${cx + halfW1},${y1} ${cx},${tipY}`;
        const midY = nextLvl ? (y1 + y2) / 2 : (y1 + tipY) / 2 + 10;
        return (
          <g key={lvl.label} opacity={s}>
            <polygon
              points={points}
              fill={lvl.color}
              fillOpacity={interpolate(s, [0, 1], [0.15, 0.82])}
              stroke="rgba(0,0,0,0.3)"
              strokeWidth={1}
            />
            <text
              x={cx}
              y={midY}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#fff"
              fontSize={24}
              fontWeight={700}
              fontFamily="system-ui, sans-serif"
              style={{ opacity: s }}
            >
              {`P${lvl.priority}  ${lvl.label}`}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const FlowchartBlock: React.FC<{
  scenario: (typeof flowScenarios)[0];
  index: number;
  frame: number;
  baseFrame: number;
  fps: number;
}> = ({ scenario, index, frame, baseFrame, fps }) => {
  const rel = frame - baseFrame;
  return (
    <div
      style={{
        flex: 1,
        padding: '22px 20px',
        borderRadius: 18,
        background: 'rgba(255,255,255,0.04)',
        border: `2px solid ${scenario.color}44`,
      }}
    >
      <div
        style={{
          fontSize: 34,
          fontWeight: 800,
          color: scenario.color,
          marginBottom: 18,
          textAlign: 'center',
        }}
      >
        {scenario.title}
      </div>
      {scenario.steps.map((step, si) => {
        const s = spring({
          frame: rel - index * 18 - si * 10,
          fps,
          config: { damping: 11, stiffness: 170 },
        });
        return (
          <div
            key={step}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 14,
              opacity: s,
              transform: `translateX(${interpolate(s, [0, 1], [-14, 0])}px)`,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: scenario.color,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 26,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              {si + 1}
            </div>
            <div
              style={{
                fontSize: 28,
                color: 'rgba(255,255,255,0.85)',
                lineHeight: 1.4,
              }}
            >
              {step}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const EmergencyPrepAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const opTitle = sectionOpacity(frame, 10, 78, FADE);
  const opWhy = sectionOpacity(frame, 88, 170, FADE);
  const opKit = sectionOpacity(frame, 180, 282, FADE);
  const opPyramid = sectionOpacity(frame, 292, 378, FADE);
  const opFlow = sectionOpacity(frame, 388, 472, FADE);
  const opHotlines = sectionOpacity(frame, 482, 530, FADE);

  const slideTitle = sectionSlide(frame, 10, 78, FADE);
  const slideWhy = sectionSlide(frame, 88, 170, FADE);
  const slideKit = sectionSlide(frame, 180, 282, FADE);
  const slidePyramid = sectionSlide(frame, 292, 378, FADE);
  const slideFlow = sectionSlide(frame, 388, 472, FADE);
  const slideHotlines = sectionSlide(frame, 482, 530, FADE);

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(155deg, ${BG} 0%, #0B1120 40%, #1a0a0a 100%)`,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 65% 50% at 80% 15%, rgba(239,68,68,0.15), transparent 55%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 50% 45% at 10% 85%, rgba(245,158,11,0.1), transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      {/* Section 1: Title */}
      {opTitle > 0 && (
        <AbsoluteFill
          style={{
            opacity: opTitle,
            transform: `translateY(${slideTitle}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 24,
          }}
        >
          <CountdownRing frame={frame} baseFrame={0} />
          <h1
            style={{
              margin: 0,
              fontSize: 76,
              fontWeight: 800,
              letterSpacing: -0.5,
              background: `linear-gradient(110deg, ${PRIMARY}, ${SECONDARY})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            应急准备指南
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 32,
              color: 'rgba(255,255,255,0.5)',
              fontWeight: 500,
            }}
          >
            关键时刻自救 · Emergency Kit &amp; Response
          </p>
        </AbsoluteFill>
      )}

      {/* Section 2: Why Prepare */}
      {opWhy > 0 && (
        <AbsoluteFill
          style={{
            opacity: opWhy,
            transform: `translateY(${slideWhy}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 72px',
          }}
        >
          <h2
            style={{
              margin: '0 0 80px',
              fontSize: 44,
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            为何要提前准备
          </h2>
          <div
            style={{
              display: 'flex',
              gap: 28,
              width: '100%',
              maxWidth: 1200,
            }}
          >
            {prepReasons.map((r, i) => {
              const s = spring({
                frame: frame - 90 - i * 12,
                fps,
                config: { damping: 12, stiffness: 180 },
              });
              return (
                <div
                  key={r.label}
                  style={{
                    flex: 1,
                    padding: '28px 24px',
                    borderRadius: 16,
                    background: 'rgba(239,68,68,0.08)',
                    border: `1px solid ${PRIMARY}40`,
                    opacity: s,
                    transform: `scale(${interpolate(s, [0, 1], [0.92, 1])})`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 44,
                      fontWeight: 900,
                      color: PRIMARY,
                      marginBottom: 10,
                    }}
                  >
                    {r.stat}
                  </div>
                  <div
                    style={{
                      fontSize: 30,
                      fontWeight: 700,
                      color: '#fff',
                      marginBottom: 8,
                    }}
                  >
                    {r.label}
                  </div>
                  <div
                    style={{
                      fontSize: 28,
                      color: 'rgba(255,255,255,0.5)',
                      lineHeight: 1.5,
                    }}
                  >
                    {r.sub}
                  </div>
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      )}

      {/* Section 3: Emergency Kit */}
      {opKit > 0 && (
        <AbsoluteFill
          style={{
            opacity: opKit,
            transform: `translateY(${slideKit}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 72px',
          }}
        >
          <h2
            style={{
              margin: '0 0 80px',
              fontSize: 44,
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            家庭应急包清单
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 16,
              width: '100%',
              maxWidth: 1300,
            }}
          >
            {kitItems.map((item, i) => {
              const s = spring({
                frame: frame - 185 - i * 10,
                fps,
                config: { damping: 13, stiffness: 200 },
              });
              const check = spring({
                frame: frame - 195 - i * 10,
                fps,
                config: { damping: 10, stiffness: 220 },
              });
              return (
                <div
                  key={item.name}
                  style={{
                    padding: '16px 18px',
                    borderRadius: 14,
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(245,158,11,0.25)',
                    opacity: s,
                    transform: `translateY(${interpolate(s, [0, 1], [18, 0])}px)`,
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      border: `2px solid ${ACCENT}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: 6,
                      background: check > 0.55 ? `${ACCENT}22` : 'transparent',
                    }}
                  >
                    <svg
                      width={16}
                      height={16}
                      viewBox="0 0 24 24"
                      style={{ opacity: check }}
                    >
                      <path
                        d="M5 12l5 5L20 7"
                        fill="none"
                        stroke={ACCENT}
                        strokeWidth={3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: 30, fontWeight: 700, color: '#fff' }}>
                      {item.name}
                    </div>
                    <div
                      style={{
                        fontSize: 26,
                        color: 'rgba(255,255,255,0.45)',
                        marginTop: 4,
                      }}
                    >
                      {item.detail}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      )}

      {/* Section 4: Priority Pyramid */}
      {opPyramid > 0 && (
        <AbsoluteFill
          style={{
            opacity: opPyramid,
            transform: `translateY(${slidePyramid}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <h2
            style={{
              margin: '0 0 8px',
              fontSize: 44,
              color: '#fff',
              fontWeight: 700,
            }}
          >
            应急响应优先级
          </h2>
          <p
            style={{
              margin: '0 0 16px',
              fontSize: 28,
              color: 'rgba(255,255,255,0.4)',
            }}
          >
            示意金字塔 · 上方优先级最高
          </p>
          <PriorityPyramid frame={frame} baseFrame={296} fps={fps} />
        </AbsoluteFill>
      )}

      {/* Section 5: Scenario Flow */}
      {opFlow > 0 && (
        <AbsoluteFill
          style={{
            opacity: opFlow,
            transform: `translateY(${slideFlow}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 60px',
          }}
        >
          <h2
            style={{
              margin: '0 0 80px',
              fontSize: 44,
              color: '#fff',
              fontWeight: 700,
            }}
          >
            情景应对流程
          </h2>
          <div
            style={{
              display: 'flex',
              gap: 24,
              width: '100%',
              maxWidth: 1200,
            }}
          >
            {flowScenarios.map((sc, i) => (
              <FlowchartBlock
                key={sc.title}
                scenario={sc}
                index={i}
                frame={frame}
                baseFrame={392}
                fps={fps}
              />
            ))}
          </div>
        </AbsoluteFill>
      )}

      {/* Section 6: Hotlines */}
      {opHotlines > 0 && (
        <AbsoluteFill
          style={{
            opacity: opHotlines,
            transform: `translateY(${slideHotlines}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 32,
          }}
        >
          <div style={{ display: 'flex', gap: 32 }}>
            {hotlines.map((h, i) => {
              const s = spring({
                frame: frame - 484 - i * 8,
                fps,
                config: { damping: 11, stiffness: 200 },
              });
              return (
                <div
                  key={h.num}
                  style={{
                    padding: '28px 56px',
                    borderRadius: 16,
                    background: 'rgba(255,255,255,0.06)',
                    border: `2px solid ${h.color}55`,
                    textAlign: 'center',
                    opacity: s,
                    minWidth: 180,
                  }}
                >
                  <div
                    style={{
                      fontSize: 52,
                      fontWeight: 900,
                      color: h.color,
                      letterSpacing: 2,
                    }}
                  >
                    {h.num}
                  </div>
                  <div
                    style={{
                      fontSize: 26,
                      color: 'rgba(255,255,255,0.55)',
                      marginTop: 8,
                    }}
                  >
                    {h.name}
                  </div>
                </div>
              );
            })}
          </div>
          <div
            style={{
              padding: '20px 36px',
              borderRadius: 14,
              background: `${ACCENT}18`,
              border: `1px solid ${ACCENT}55`,
              maxWidth: 1000,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: 30,
                fontWeight: 700,
                color: ACCENT,
                marginBottom: 6,
              }}
            >
              牢记关键号码
            </div>
            <div
              style={{
                fontSize: 24,
                color: 'rgba(255,255,255,0.55)',
                lineHeight: 1.55,
              }}
            >
              保存本地应急广播与社区联络方式；演练时与家人约定集合点。
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'rgba(255,255,255,0.06)',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${(frame / 540) * 100}%`,
            background: `linear-gradient(90deg, ${PRIMARY}, ${SECONDARY}, ${ACCENT})`,
          }}
        />
      </div>
    </AbsoluteFill>
  );
};
